import { useState, useEffect, useMemo, useCallback } from 'react';
import intl from 'react-intl-universal';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import get from 'lodash/get';
import {
  Button,
  Typography,
  Popup,
  List,
  Icon,
  tailwindOverride,
} from '@getsynapse/design-system';
import { isUserOrganizationAdmin, isLDTeamManager } from 'state/User/userSlice';
import PageTitle from 'Molecules/PageTitle/PageTitle';
import goalsEmptyState from 'assets/images/goals-empty-state.svg';
import AddGoalModal from './components/AddGoalModal/AddGoalModal';
import LoadingError from 'Molecules/LoadingError/LoadingError';
import SkeletonLoader from './components/SkeletonLoader';
import {
  fetchStrategyGoals,
  selectStrategyGoals,
  selectStrategyGoalsStatus,
  getGoalsProgress,
  selectProgressStatus,
  setTimePeriodFilters,
} from 'state/StrategyGoals/StrategyGoalsSlice';
import { SLICE_STATUS } from 'utils/constants';
import GoalCard from './components/GoalCard/GoalCard';
import { GoalTeam, StrategyGoal } from 'utils/types/strategyGoals';
import { GOAL_TYPES } from 'utils/constants/strategyGoals';
import { SETTINGS_FILTERS } from 'utils/constants';
import {
  selectFiltersSettingsByType,
  updateFilterSetting,
  createFilterSetting,
  fetchFilterSettingByType,
} from 'state/Settings/Filters/FiltersSlice';
import classnames from 'classnames';
import {
  fetchYearTimePeriods,
  selectYears,
  selectTimePeriods,
} from 'state/YearTimePeriods/YearTimePeriodSlice';
import { generateTimeOptions } from './helpers';

const StrategyGoalsPage = () => {
  const dispatch = useDispatch();

  const strategyGoalsStatus = useSelector(selectStrategyGoalsStatus);
  const progressStatus = useSelector(selectProgressStatus);
  const strategyGoals = useSelector(selectStrategyGoals);
  const isUserAdmin = useSelector(isUserOrganizationAdmin);
  const isUserTeamManager = useSelector(isLDTeamManager);
  const [filtersAreFetched, setFiltersAreFetched] = useState(false);
  const [isAddGoalModalOpen, setIsAddGoalModalOpen] = useState(false);

  const years = useSelector(selectYears);
  const timePeriods = useSelector(selectTimePeriods);
  const goals = useMemo(() => {
    const groupedGoals: Record<string, StrategyGoal[]> = {};

    strategyGoals.forEach((goal) => {
      let key = '';
      if (goal.type === GOAL_TYPES.COMPANY) {
        key = intl.get('STRATEGY_GOALS.COMPANY_GOAL');
      } else if (goal.type === GOAL_TYPES.TEAM && goal.teams.length > 0) {
        key = (goal.teams[0] as GoalTeam).name;
      } else {
        key = intl.get('STRATEGY_GOALS.FORMER_TEAM');
      }

      if (groupedGoals[key]) {
        groupedGoals[key].push(goal);
      } else {
        groupedGoals[key] = [goal];
      }
    });

    return groupedGoals;
  }, [strategyGoals]);

  const hasGoals = strategyGoals.length > 0 && Object.keys(goals).length > 0;

  const filtersSettings = useSelector(
    selectFiltersSettingsByType(SETTINGS_FILTERS.STRATEGY_GOALS_TIMEPERIOD)
  );

  const savedFilters = useMemo(
    () => (filtersSettings ? filtersSettings.settings : {}),
    [filtersSettings]
  );

  const fetchGoalsAndTheirProgress = useCallback(async () => {
    const resultAction = await dispatch(
      fetchStrategyGoals({
        timePeriod: savedFilters.timePeriod,
        year: savedFilters.year,
      })
    );
    const goals = get(resultAction, 'payload');

    const goalIds = goals?.map((goal: StrategyGoal) => goal.id);
    if (goalIds?.length > 0) dispatch(getGoalsProgress(goalIds));
  }, [dispatch, savedFilters]);

  useEffect(() => {
    if (!timePeriods?.length) {
      dispatch(fetchYearTimePeriods());
    }
  }, [dispatch, timePeriods?.length]);

  useEffect(() => {
    const fetchFiltersAreFetched = async () => {
      await dispatch(
        fetchFilterSettingByType(SETTINGS_FILTERS.STRATEGY_GOALS_TIMEPERIOD)
      );
      setFiltersAreFetched(true);
    };
    if (!filtersAreFetched) fetchFiltersAreFetched();
  }, [dispatch, filtersAreFetched]);

  useEffect(() => {
    filtersAreFetched && fetchGoalsAndTheirProgress();
  }, [filtersAreFetched, fetchGoalsAndTheirProgress]);
  const openAddGoalModal = () => setIsAddGoalModalOpen(true);
  const closeAddGoalModal = () => setIsAddGoalModalOpen(false);

  const saveFilters = (updatedFilters: {
    timePeriod: string;
    year: string;
  }) => {
    if (filtersSettings && filtersSettings.id) {
      dispatch(
        updateFilterSetting({
          id: filtersSettings.id,
          updateFields: { filter_settings: updatedFilters },
        })
      );
    } else {
      dispatch(
        createFilterSetting({
          filter_type: SETTINGS_FILTERS.STRATEGY_GOALS_TIMEPERIOD,
          filter_settings: updatedFilters,
        })
      );
    }
  };
  const onSelectTimePeriodChange = (option: {
    label: string;
    value: { timePeriod: string; year: string };
  }) => {
    const updatedFilters = {
      timePeriod: option.value?.timePeriod,
      year: option.value?.year,
    };
    setTimePeriodFilters(updatedFilters);
    saveFilters(updatedFilters);
  };

  const generatePopupList = (option: any, close: () => void) => {
    return (
      <li
        role='option'
        aria-selected={false}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            onSelectTimePeriodChange({
              label: '',
              value: {
                timePeriod: option.value.timePeriod,
                year: option.value.year,
              },
            });
            close();
          }
        }}
        onClick={() => {
          onSelectTimePeriodChange({
            label: '',
            value: {
              timePeriod: option.value.timePeriod,
              year: option.value.year,
            },
          });
          close();
        }}
        className={classnames(
          'group hover:border-l-2 flex w-full py-2 px-4 hover:bg-secondary-lightest cursor-pointer',
          `${
            option.label.includes(savedFilters.timePeriod) &&
            option.label.includes(savedFilters.year)
              ? 'bg-primary text-neutral-white'
              : ''
          }`
        )}
      >
        <Typography
          variant='label'
          className={tailwindOverride(
            {
              'text-neutral-white':
                option.label.includes(savedFilters.timePeriod) &&
                option.label.includes(savedFilters.year),
            },
            {
              'hover:border-secondary-lighter flex flex-start group-hover:text-secondary-darker text-sm font-normal font-semibold':
                option.label.startsWith('FY'),
            },
            {
              'hover:border-secondary-lighter flex px-4 group-hover:text-secondary-darker text-sm font-normal items-center':
                !option.label.startsWith('FY'),
            }
          )}
        >
          {option.label}
        </Typography>
      </li>
    );
  };

  const renderOptions = () => {
    const timePeriodOptions = [...generateTimeOptions(years, timePeriods)];
    const currentYear = new Date().getFullYear();
    const savedFiltersTimePeriod = savedFilters.timePeriod
      ? `${savedFilters.timePeriod} ${savedFilters.year}`
      : intl.get('STRATEGY_GOALS.FISCAL_YEAR', { year: currentYear });

    return (
      <Popup
        position='bottom-start'
        popperProps={{ className: 'z-10' }}
        trigger={({ isOpen }) => (
          <div className=' cursor-pointer flex flex-row items-center gap-x-2'>
            <div data-cy='saved-filters-time-period'>{`${savedFiltersTimePeriod}`}</div>
            {isOpen ? (
              <Icon name='caret-up-outline' />
            ) : (
              <Icon name='caret-down-outline' />
            )}
          </div>
        )}
      >
        {({ close }) => (
          <List
            options={timePeriodOptions ?? []}
            className='max-h-60 overflow-y-scroll'
            data-cy='time-period-actions-list'
            renderOption={(option) => generatePopupList(option, close)}
            onSelectOption={() => {}}
          />
        )}
      </Popup>
    );
  };

  return (
    <>
      {(isUserAdmin || isUserTeamManager) && (
        <AddGoalModal
          isOpen={isAddGoalModalOpen}
          closeModal={closeAddGoalModal}
          fetchGoalsAndTheirProgress={fetchGoalsAndTheirProgress}
        />
      )}
      <div className='flex items-center justify-between px-6'>
        <PageTitle
          titleComponent={
            <div className='flex items-center'>
              <span>{intl.get('STRATEGY_GOALS.HEADER_TITLE')}</span>
              <span className='ml-4 font-normal	text-base'>
                {renderOptions()}
              </span>
            </div>
          }
          className='p-0'
        />
        {(isUserAdmin || isUserTeamManager) && (
          <Button data-testid='add-goal_button' onClick={openAddGoalModal}>
            {intl.get('STRATEGY_GOALS.ADD_GOAL')}
          </Button>
        )}
      </div>
      {(strategyGoalsStatus === SLICE_STATUS.LOADING ||
        progressStatus === SLICE_STATUS.LOADING) && <SkeletonLoader />}
      {strategyGoalsStatus === SLICE_STATUS.IDLE &&
        progressStatus === SLICE_STATUS.IDLE && (
          <div
            className={classNames(
              'h-goals overflow-y-auto flex flex-col w-full items-center px-6 pb-6',
              {
                'justify-center ': !hasGoals,
              }
            )}
            data-testid='testing-goals'
          >
            {!hasGoals && (
              <>
                <img src={goalsEmptyState} alt='' className='mb-6' />
                <Typography>
                  {intl.get('STRATEGY_GOALS.EMPTY_STATE')}
                </Typography>
              </>
            )}
            {hasGoals && (
              <div className='space-y-6 w-full'>
                {Object.keys(goals).map((key, index) => (
                  <div key={index}>
                    <Typography
                      variant='h6'
                      className={classNames('mb-2', {
                        'text-neutral':
                          key === intl.get('STRATEGY_GOALS.FORMER_TEAM'),
                      })}
                      data-testid='goal-group'
                    >
                      {key}
                    </Typography>
                    <div className='space-y-2'>
                      {goals[key].map((goal) => (
                        <GoalCard key={goal.id} goal={goal} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      {(strategyGoalsStatus === SLICE_STATUS.FAILED ||
        progressStatus === SLICE_STATUS.FAILED) && (
        <LoadingError headerHeight='6rem' className='px-6' errorCode={500} />
      )}
    </>
  );
};

export default StrategyGoalsPage;
